import { getAuthProviderFetchJson } from '../Auth';
import { superAdminApiServer } from '../config';
import { DateRange, RecalculateResponse, CreatePunchesRequest, CreatePunchesResponse, DayDataResponse, DeclinePunchesResponse } from '../../Pages/DevTools/Recalculate/types';

/** superadmin-server proxy to day-global-calculations' manual recalculate (synchronous; upstream snapshots the fetched sources and writes the run's shift/day results, unless `policy` names a template to preview instead of the assigned one). */
export const recalculate = async (companyUuid: string, userProfileUuid: string, range: DateRange): Promise<RecalculateResponse> => {
  const { json } = await getAuthProviderFetchJson(`${superAdminApiServer}/global-calculations/companies/${companyUuid}/user_profiles/${userProfileUuid}/recalculate`, {
    method: 'POST',
    // force: upstream skips a shift whose stored result already matches its inputs and returns
    // it with no outputs, so any range calculated once renders an empty report. This tool exists
    // to show the numbers, so it always asks for them. It costs nothing in storage: a forced run
    // writes the rows that were there already, and the upsert suppresses an identical row.
    body: JSON.stringify({ ...range, force: true })
  });
  if (!json || typeof json !== 'object' || !json.content || !json.metadata) throw new Error('Unexpected recalculate response shape');
  return json as RecalculateResponse;
};

export const loadDayData = async (companyUuid: string, userProfileUuid: string, range: DateRange): Promise<DayDataResponse> => {
  const params = new URLSearchParams({ from: range.from, to: range.to });
  const { json } = await getAuthProviderFetchJson(`${superAdminApiServer}/dev-tools/punching/companies/${companyUuid}/user_profiles/${userProfileUuid}/day-data?${params}`);
  if (!json || typeof json !== 'object' || !json.context || !json.days) throw new Error('Unexpected day-data response shape');
  return json as DayDataResponse;
};

/** Punches per create-punches request. Each punch costs three upstream calls, and nginx's default
 * ingress timeout is 60s, so a full 200-punch request can time out before it finishes. Batching
 * keeps each call fast and lets a partial failure report how far it got. */
export const CREATE_PUNCHES_BATCH_SIZE = 25;

// Splits punches into batches of at most `size`. An empty array stays a single empty batch, so a
// zero-punch request still makes one call, same as before batching existed.
const chunkPunches = (punches: CreatePunchesRequest['punches'], size: number): CreatePunchesRequest['punches'][] => {
  if (punches.length === 0) return [[]];
  const chunks: CreatePunchesRequest['punches'][] = [];
  for (let i = 0; i < punches.length; i += size) chunks.push(punches.slice(i, i + size));
  return chunks;
};

const sendCreatePunchesChunk = async (companyUuid: string, userProfileUuid: string, payload: CreatePunchesRequest): Promise<CreatePunchesResponse> => {
  const { json } = await getAuthProviderFetchJson(`${superAdminApiServer}/dev-tools/punching/companies/${companyUuid}/user_profiles/${userProfileUuid}/punches`, {
    method: 'POST',
    body: JSON.stringify(payload)
  });
  if (!json || typeof json !== 'object' || !Array.isArray(json.created) || !Array.isArray(json.failed)) throw new Error('Unexpected create-punches response shape');
  return json as CreatePunchesResponse;
};

// Sends the punches in batches of CREATE_PUNCHES_BATCH_SIZE, sequentially, with the same
// createdBy/requestedBy/reason on each call, merging created/failed in order. If a batch throws
// after earlier ones succeeded, the error says how many punches were already created so the
// dialog can show progress instead of a bare failure.
export const createPunches = async (companyUuid: string, userProfileUuid: string, payload: CreatePunchesRequest): Promise<CreatePunchesResponse> => {
  const total = payload.punches.length;
  const created: CreatePunchesResponse['created'] = [];
  const failed: CreatePunchesResponse['failed'] = [];

  for (const punches of chunkPunches(payload.punches, CREATE_PUNCHES_BATCH_SIZE)) {
    try {
      const chunkResponse = await sendCreatePunchesChunk(companyUuid, userProfileUuid, { ...payload, punches });
      created.push(...chunkResponse.created);
      failed.push(...chunkResponse.failed);
    } catch (error) {
      // Nothing was created yet: rethrow as-is so a plain failure (e.g. "Employee X is not
      // active") is not disguised as a partial-batch error.
      if (created.length === 0) throw error;
      const originalMessage = error instanceof Error ? error.message : String(error);
      throw new Error(`Created ${created.length} of ${total} punches before the error: ${originalMessage}`);
    }
  }

  return { created, failed };
};

export const declinePunches = async (companyUuid: string, userProfileUuid: string, punchUuids: string[]): Promise<DeclinePunchesResponse> => {
  const { json } = await getAuthProviderFetchJson(`${superAdminApiServer}/dev-tools/punching/companies/${companyUuid}/user_profiles/${userProfileUuid}/punches/decline`, {
    method: 'PATCH',
    body: JSON.stringify({ punchUuids })
  });
  if (!json || typeof json !== 'object' || typeof json.declined !== 'number' || !Array.isArray(json.failed) || !Array.isArray(json.railsSkipped) || !Array.isArray(json.railsFailed)) throw new Error('Unexpected decline-punches response shape');
  return json as DeclinePunchesResponse;
};
